import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { type AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useProductsWithoutStock } from '../../hooks/useProductsWithoutStock';
import { ProductsWithoutStockApiAdapter } from '../../adapters/ProductsWithoutStockApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ErrorState, LoadingState } from '../feedback';
import { CardList, ProductsWithoutStockAgGridTable, Report } from '../display';
import { FiltersSection, AdditionalFilters } from '../interaction';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { useToast } from '@shared/hooks/use-toast';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const Main = () => {
  const { t } = useTranslation('productsWithoutStockModule');
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    shop: { type: 'string', default: '' },
    paymentMethod: { type: 'string', default: '' },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [selectedShop, setSelectedShop] = useState(queryParams.shop ?? '');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(queryParams.paymentMethod ?? '');
  const [sorting, setSorting] = useState<SortingConfig | null>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  }));

  // 🎯 Efectos para sincronizar estados locales con URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setSelectedShop(queryParams.shop ?? '');
  }, [queryParams.shop]);

  useEffect(() => {
    setSelectedPaymentMethod(queryParams.paymentMethod ?? '');
  }, [queryParams.paymentMethod]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const processedFilters = useMemo(() => {
    return {
      ...(selectedShop && { shop_id: Number(selectedShop) }),
      ...(selectedPaymentMethod && {
        payment_method_id: Number(selectedPaymentMethod),
      }),
      sortBy: sorting?.field ?? 'id',
      sortDirection: sorting?.direction ?? 'desc',
    };
  }, [selectedShop, selectedPaymentMethod, sorting]);

  const {
    productsWithoutStock,
    summary,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useProductsWithoutStock({
    searchTerm,
    ...processedFilters,
  });

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const hasActiveFilters = useMemo(() => {
    return searchTerm.trim() !== '' || selectedShop !== '' || selectedPaymentMethod !== '';
  }, [searchTerm, selectedShop, selectedPaymentMethod]);

  // 🎯 Handlers optimizados para evitar bucles infinitos
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleShopChange = useCallback(
    (shopId: string) => {
      setSelectedShop(shopId);
      if (queryParams.shop !== shopId) {
        updateQueryParams({ shop: shopId || undefined });
      }
    },
    [updateQueryParams, queryParams.shop],
  );

  const handlePaymentMethodChange = useCallback(
    (paymentMethodId: string) => {
      setSelectedPaymentMethod(paymentMethodId);
      if (queryParams.paymentMethod !== paymentMethodId) {
        updateQueryParams({ paymentMethod: paymentMethodId || undefined });
      }
    },
    [updateQueryParams, queryParams.paymentMethod],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingConfig | null) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'id';
      const sortDirection = newSorting?.direction || 'desc';

      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateQueryParams({
          sortBy: sortBy === 'id' ? undefined : sortBy,
          sortDirection: sortDirection === 'desc' ? undefined : sortDirection,
        });
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloading(true);

    try {
      const apiAdapter = new ProductsWithoutStockApiAdapter(createHttpClient(apiClientWH));
      const { blob, filename } = await apiAdapter.downloadExcel({
        searchTerm,
        ...processedFilters,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: t('toasts.download_success'),
        variant: 'default',
      });
    } catch {
      toast({
        title: t('toasts.error.title'),
        description: t('toasts.error.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [searchTerm, processedFilters, toast, t]);

  const productsWithoutStockViewProps = useMemo(
    () => ({
      productsWithoutStock,
      isLoading: isInitialLoad && isLoading,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [productsWithoutStock, isInitialLoad, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const isBackgroundFetching = !isInitialLoad && isFetching && !isFetchingNextPage;

  const renderProductsWithoutStock = () => {
    if (isMobile) {
      return <CardList {...productsWithoutStockViewProps} showActions={true} />;
    }

    return (
      <ProductsWithoutStockAgGridTable
        {...productsWithoutStockViewProps}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <Report summary={summary} />
      <div className="mb-4">
        <div className="flex items-center overflow-x-auto flex-wrap justify-between gap-3">
          <FiltersSection
            handleSearch={handleSearch}
            handleDownloadExcel={handleDownloadExcel}
            isDownloading={isDownloading}
          />
          <AdditionalFilters
            onShopChange={handleShopChange}
            onPaymentMethodChange={handlePaymentMethodChange}
            selectedShop={selectedShop}
            selectedPaymentMethod={selectedPaymentMethod}
          />
        </div>
      </div>
      {isLoading && <LoadingState message={t('loading.message')} />}
      {!isLoading && (
        <div className="relative mb-4">
          <div
            className={`transition-opacity duration-300 ${
              isBackgroundFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            {productsWithoutStock.length > 0 && renderProductsWithoutStock()}
          </div>

          {productsWithoutStock.length === 0 && (
            <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.PRODUCTS} />
          )}
        </div>
      )}
    </div>
  );
};
